import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import { getLogoPath, getCollegeInitials } from '../utils/collegeUtils';

import './FullProfileView.css';

export default function FullProfileView() {
  const { id } = useParams(); // Get profile ID from URL
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Helper function to render college logo
  const renderCollegeLogo = (collegeName) => {
    const logoPath = getLogoPath(collegeName);
    
    return (
      <div className="college-logo-container">
        {logoPath ? (
          <img
            src={logoPath}
            alt={`${collegeName} logo`}
            className="college-logo"
            onError={(e) => {
              const fallback = e.target.nextElementSibling;
              if (fallback) fallback.style.display = 'inline-flex';
              e.target.style.display = 'none';
              e.target.onerror = null;
            }}
          />
        ) : null}
        <span 
          className="college-logo-fallback"
          style={{ display: logoPath ? 'none' : 'inline-flex' }}
        >
          {getCollegeInitials(collegeName)}
        </span>
      </div>
    );
  };

  useEffect(() => {
    const fetchFullProfile = async () => {
      setLoading(true);
      setError(null);
      try {
        // Get current user
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) {
          throw new Error('You must be logged in to view profiles.');
        }

        // First, get the profile data
        const { data: profileData, error: profileError } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', id)
          .single();

        if (profileError) {
          if (profileError.code === 'PGRST116') {
            throw new Error('Profile not found.');
          }
          throw profileError;
        }

        // Archived duplicates retain their original records; point viewers to the canonical profile.
        if (profileData.is_archived && profileData.duplicate_of) {
          window.location.replace(`/profile/${profileData.duplicate_of}`);
          return;
        }

        // Check if user has access (owns profile or has unlocked it)
        let hasAccess = profileData.user_id === user.id; // User owns the profile

        if (!hasAccess) {
          // Check if profile is unlocked
          const { data: unlockData, error: unlockError } = await supabase
            .from('unlocked_profiles')
            .select('id')
            .eq('user_id', user.id)
            .eq('profile_id', id)
            .maybeSingle();

          if (unlockError && unlockError.code !== 'PGRST116') {
            throw unlockError;
          }

          hasAccess = !!unlockData;
        }

        if (!hasAccess) {
          throw new Error('Access denied. You need to unlock this profile first.');
        }

        setProfile(profileData);
      } catch (err) {
        console.error("Error fetching full profile:", err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchFullProfile();
    } else {
      setError("No profile ID provided.");
      setLoading(false);
    }
  }, [id]);

  if (loading) return <div className="loading-container">Loading profile...</div>;
  
  if (error) return <div className="error-container">Error: {error} <Link to="/leaderboard">Back to Leaderboard</Link></div>;
  
  if (!profile) return <div>Profile not found.</div>;

  // --- Render Full Profile Details ---
  // (Adapt this structure based on your actual 'profiles' table columns)
  return (
    <div className="full-profile-view container">
      <h2>Profile Details (Class of {profile.class_year})</h2>
      <p>Rating: {Math.round(profile.rating)}</p>

      <section className="profile-section stats-section">
        <h3>Academic Stats</h3>
        <div className="stats-grid">
          <div className="stat-item">
            <span className="stat-label">GPA</span>
            <span className="stat-value">{profile.gpa?.toFixed(2) || 'N/A'}</span>
          </div>
          <div className="stat-item">
            <span className="stat-label">SAT</span>
            <span className="stat-value">{profile.sat_score || 'N/A'}</span>
          </div>
          <div className="stat-item">
            <span className="stat-label">ACT</span>
            <span className="stat-value">{profile.act_score || 'N/A'}</span>
          </div>
          <div className="stat-item">
            <span className="stat-label">APs Taken</span>
            <span className="stat-value">{profile.ap_num_taken || 'N/A'}</span>
          </div>
          <div className="stat-item">
            <span className="stat-label">AP 5s / IB 7s</span>
            <span className="stat-value">{profile.ap_num_fives || 'N/A'}</span>
          </div>
          {profile.special_program && (
            <div className="stat-item special-program">
              <span className="stat-label">Special Program</span>
              <span className="stat-value">{profile.special_program}</span>
            </div>
          )}
        </div>
      </section>

      <section className="profile-section">
        <h3>{profile.main_essay_type || 'Essay'}{profile.main_prompt_school ? ` (${profile.main_prompt_school})` : ''}</h3>
        {profile.main_essay_prompt && <p><strong>Prompt:</strong> {profile.main_essay_prompt}</p>}
        {/* Render Markdown or plain text safely */}
        <pre className="essay-content">{profile.essay}</pre>
      </section>

      {profile.extracurriculars && profile.extracurriculars.length > 0 && (
         <section className="profile-section">
            <h3>Extracurriculars</h3>
            <ul>
                {profile.extracurriculars.map((ec, index) => <li key={index}>{ec}</li>)}
            </ul>
         </section>
      )}

      {profile.awards && (
         <section className="profile-section">
            <h3>Awards</h3>
            <p>{profile.awards}</p>
         </section>
      )}

      {profile.college_results && Object.keys(profile.college_results).length > 0 && (
         <section className="profile-section">
             <h3>College Results</h3>
             <div className="college-results-grid">
                {Object.entries(profile.college_results)
                  .filter(([, status]) => status.toLowerCase() !== 'rejected') // Exclude rejections
                  .sort(([, statusA], [, statusB]) => {
                    // Sort by status priority: Accepted > Committed > Waitlisted
                    const statusOrder = { 'Committed': 0, 'Accepted': 1, 'Waitlisted': 2 };
                    return (statusOrder[statusA] || 3) - (statusOrder[statusB] || 3);
                  })
                  .map(([college, status]) => (
                    <div key={college} className={`college-result-card ${status.toLowerCase()}`}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
                        {renderCollegeLogo(college)}
                        <div className="college-name">{college}</div>
                      </div>
                      <div className="college-status">
                        {status}
                        {status.toLowerCase() === 'committed' && <span className="celebration">🎉</span>}
                      </div>
                    </div>
                ))}
             </div>
         </section>
      )}

      {/* Handle both old and new supplemental essay formats */}
      {profile.supplemental_essays?.schools && (
         <section className="profile-section">
           <h3>Supplemental Essays</h3>
           {Object.entries(profile.supplemental_essays.schools).map(([school, schoolData]) => (
             <div key={school} className="school-essays-container">
               <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem' }}>
                 {renderCollegeLogo(school)}
                 <h4 className="school-essays-title" style={{ margin: 0 }}>{school}</h4>
               </div>
               {schoolData.essays && Object.entries(schoolData.essays).map(([essayId, essayContent]) => (
                 <div key={essayId} className="supplemental-essay-item">
                   <h5>{essayId.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</h5>
                   <pre className="essay-content">{essayContent}</pre>
                 </div>
               ))}
             </div>
           ))}
         </section>
      )}
      
      {/* Legacy format support */}
      {profile.supplemental_essays?.college && !profile.supplemental_essays?.schools && (
         <section className="profile-section">
           <h3>Supplemental Essays ({profile.supplemental_essays.college})</h3>
           {profile.supplemental_essays.prompts && Object.entries(profile.supplemental_essays.prompts).map(([promptId, essayData]) => (
              <div key={promptId} className="supplemental-essay-item">
                 <h4>{essayData.title}</h4>
                 <p><i>{essayData.prompt}</i></p>
                 <pre className="essay-content">{essayData.response}</pre>
                 <hr/>
              </div>
           ))}
           {profile.supplemental_essays.essays && profile.supplemental_essays.essays.map((essayData, index) => (
              <div key={index} className="supplemental-essay-item">
                 <h4>Essay #{essayData.index + 1}</h4>
                 <pre className="essay-content">{essayData.response}</pre>
                 <hr/>
              </div>
           ))}
         </section>
      )}

      <div className="profile-navigation" style={{ 
        display: 'flex', 
        gap: '1rem', 
        justifyContent: 'center', 
        marginTop: '2rem', 
        padding: '1rem', 
        borderTop: '1px solid #eee' 
      }}>
        <Link to="/leaderboard" style={{ 
          color: '#007bff', 
          textDecoration: 'none', 
          fontWeight: '500' 
        }}>← Back to Leaderboard</Link>
        <Link to="/unlocked-profiles" style={{ 
          color: '#28a745', 
          textDecoration: 'none', 
          fontWeight: '500' 
        }}>View All Unlocked Profiles →</Link>
      </div>
    </div>
  );
} 